import moment from 'moment-timezone';

/**
 * Function to check if an object is an array of strings.
 * @param obj
 * @returns {boolean}
 */
export function isArrayOfStrings(obj) {
    return Array.isArray(obj) && obj.every(item => typeof item === "string");
}

/**
 * Function to check if an object is a string.
 * @param dataToFormat
 * @returns {boolean}
 */
export function isString(dataToFormat) {
    return typeof dataToFormat === "string";
}

/**
 * Function to format bytes to the largest unit possible.
 * @param bytes - Number of bytes to be formatted. If 0 or not a number, returns 0.
 * @param decimals - Number of decimal places to be displayed. If lower than 0, defaults to 0.
 * @param useBinaryUnits - If true, uses binary units (1024 bytes = 1 KiB). If false, uses decimal units (1000 bytes = 1 KB).
 * @returns {string} - Formatted string with the largest unit possible.
 */
const formatBytes = (bytes, decimals = 0, useBinaryUnits = true) => {
    if (!+bytes) return '0'

    const k = useBinaryUnits ? 1024 : 1000
    const dm = decimals < 0 ? 0 : decimals
    const sizes = useBinaryUnits ? ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'] : ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

/**
 * Function to format a string or a list of strings into a single string.
 * @param {string | string[]} dataToFormat - Data to be formatted.
 * @returns {string} - Formatted string.
 */
const formatString = (dataToFormat) => {
    if (isString(dataToFormat)) {
        return dataToFormat;
    } else if (isArrayOfStrings(dataToFormat)) {
        return dataToFormat.join(", ");
    } else {
        return undefined;
    }
}

/**
 * Function to format a date string into a 'DD/MMM/YYYY' string.
 * @param dataToFormat - Date string to be formatted.
 * @returns {string} - Formatted date string.
 */
const formatDateTime = (dataToFormat) => {
    // possible formats:
    // 2025-01-15 17:41:58
    if (typeof dataToFormat !== "string") return undefined;

    const dateFormats = [
        'DD/MMM/YYYY',
        'D/MMM/YYYY',
        'DD/MM/YYYY',
        'D/MM/YYYY',
        'DD-MM-YYYY',
        'D-MM-YYYY',
        'YYYY-MM-DD',
        'YYYY-M-D'
    ];
    const dateTimeFormats = dateFormats.map(format => `${format} HH:mm:ss`).concat(dateFormats.map(format => `${format} H:m:s`));
    const dateTimeOffsetFormats = dateFormats.map(format => `${format} HH:mm:ssZ`).concat(dateFormats.map(format => `${format} H:m:sZ`));
    // Additional ISO formats with a 'T' separator and optional fractional seconds. 2025-01-08T07:30:50.215314Z
    const isoFormats = [
        "YYYY-MM-DD[T]HH:mm:ssZ",
        "YYYY-MM-DD[T]HH:mm:ss.SSSZ",
        "YYYY-MM-DD[T]HH:mm:ss.SSSSSSZ"
    ];
    const allFormats = [...dateFormats, ...dateTimeFormats, ...dateTimeOffsetFormats, ...isoFormats];

    const parsedDate = moment(dataToFormat, allFormats, true);

    if (!parsedDate.isValid()) return undefined;
    return parsedDate.format('DD/MMM/YYYY');
}

/**
 * Function to format an integer as a string.
 * @param {number} dataToFormat
 * @returns {string} - Formatted integer.
 */
const formatInt = (dataToFormat) => {
    return `${dataToFormat}`;
}

/**
 * Function to format an integer as a string for a units count.
 * @param {number} dataToFormat
 * @returns {string} - Formatted integer.
 */
const formatUnits = (dataToFormat) => {
    const suffix = dataToFormat === 1 ? "unit" : "units";
    return `${dataToFormat} ${suffix}`;
}

/**
 * Function to format a number as a percentage string with two decimal places.
 * @param dataToFormat
 * @returns {`${string}%`}
 */
const formatPercentage = (dataToFormat) => {
    return `${(dataToFormat * 100).toLocaleString(undefined, {maximumFractionDigits: 2})}%`;
}

/**
 * Function to format an integer as a string with grouping of thousands.
 * @param dataToFormat
 * @returns {string}
 */
const formatGroupedInt = (dataToFormat) => {
    return new Intl.NumberFormat('en-US', {useGrouping: true})
        .format(dataToFormat)
        .replace(/,/g, ' ');
}

/**
 * Method to parse the data from the API to the internal format.
 * @param data
 * @param {string} ui_class_to_format_to - The ui_class that sets the way UI will format the data. str, data_size, date
 * @returns {string} - Parsed data as a string.
 */
/**
 * Converts a datetime string between two IANA timezones and formats the result.
 * Uses the browser's built-in Intl API — no extra dependencies, full DST support.
 *
 * @param {string} data           - Input datetime string (any parseable format)
 * @param {string} fromTz         - IANA timezone of the input (e.g. "Australia/Perth", "UTC")
 * @param {string} toTz           - IANA timezone for display/output (e.g. "Australia/Perth", "UTC")
 * @param {string} outputFormat   - "human" (default): "22/May/2026 22:30 (AWST)"
 *                                  "iso": "2026-05-22T14:30:00.000Z"
 * @returns {string}              - Formatted string, or "" for invalid input
 */
const formatDateTimeInTimezone = (data, fromTz, toTz, outputFormat = "human") => {
    if (!data) return "";
    // Parse the input as local time in fromTz — machine-independent
    const m = moment.tz(String(data).trim(), fromTz);
    if (!m.isValid()) return "";

    if (outputFormat === "iso") {
        return m.utc().toISOString();
    }

    // Human format: convert to toTz and format
    const converted = m.tz(toTz);
    const tzLabel = converted.zoneAbbr();
    return `${converted.format("DD/MMM/YYYY HH:mm")} (${tzLabel})`;
};

/**
 * Formats an integer number of seconds as a "M:SS" countdown string.
 * @param {number} seconds - Non-negative integer seconds remaining
 * @returns {string}       - e.g. "4:32", "0:01", "0:00"
 */
const formatCountdown = (seconds) => {
    const s = Math.max(0, Math.floor(Number(seconds)));
    if (isNaN(s)) return "0:00";
    const days = Math.floor(s / 86400);
    const hours = Math.floor((s % 86400) / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    if (days > 0) return `${days}D ${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    return `${minutes}:${String(secs).padStart(2, "0")}`;
};

/**
 * Method to parse the data from the API to the internal format.
 * @param data
 * @param {string} ui_class_to_format_to - The ui_class that sets the way UI will format the data.
 * @param {object} [options]             - Optional config for parameterised formatters:
 *   datetime_tz: { fromTz, toTz, outputFormat }
 * @returns {string} - Parsed data as a string.
 */
export const formatData = (data, ui_class_to_format_to, options = {}) => {
    if (!data && data !== 0) return "";
    if (!ui_class_to_format_to) return "";

    const functionMap = {
        "str": formatString,
        "filename": formatString,
        "filepath": formatString,
        "list": formatString,
        "data_size": data_size => formatBytes(data_size, 2, false),
        "data_size_iec_units": formatBytes,
        "data_size_iso_units": data_size => formatBytes(data_size, 2, false),
        "data_size_bytes": data_size => `${formatGroupedInt(data_size)} bytes`,
        "int": formatInt,
        "units": formatUnits,
        "percentage": formatPercentage,
        "files_count": data_size => `${formatGroupedInt(data_size)} files`,
        "datetime": formatDateTime,
        "date": formatDateTime,
        "duration_ms": duration => `${formatGroupedInt(duration)} μs`,
        "datetime_tz": data => formatDateTimeInTimezone(
            data,
            options?.fromTz ?? "Australia/Perth",
            options?.toTz ?? "UTC",
            options?.outputFormat ?? "human"
        ),
        "countdown": formatCountdown,
    };
    return Object.keys(functionMap).includes(ui_class_to_format_to) ? functionMap[ui_class_to_format_to](data) : "";
}

/**
 * For a given element, it returns the formatted property (found directly as a property or in "attributes") based on the column type. It caches the formatted property in the element object.
 * @param element
 * @param {string | null | undefined} property
 * @param {string} columnType
 * @returns {string}
 */
export const getFormattedProperty = (element, property, columnType) => {
    if (!element) return "";
    if (!columnType) return "";
    if (!property) return formatData(element, columnType);

    const formattedPropertyName = `__${property}_formatted_as_${columnType}`;
    const rawCacheKeyName = `__${property}_raw_cache_key`;
    const rawData = element?.attributes && element.attributes?.[property]?.value ? element.attributes[property].value : element[property];
    // Invalidate cache when the raw value has changed (supports in-flight data enrichment)
    if (element[formattedPropertyName] === undefined || element[rawCacheKeyName] !== rawData) {
        element[rawCacheKeyName] = rawData;
        element[formattedPropertyName] = formatData(rawData, columnType);
    }
    return element[formattedPropertyName];
};


export const SIZE_MULTIPLIER = {
    B: 1,
    KB: 1000,
    MB: 1000 ** 2,
    GB: 1000 ** 3,
    TB: 1000 ** 4,
    KiB: 1024,
    MiB: 1024 ** 2,
    GiB: 1024 ** 3,
    TiB: 1024 ** 4,
};

/**
 * Function to convert a string value and unit to bytes.
 * @param {string | Number} strValue - The value to be converted to bytes.
 * @param {string} unit - The unit of the value (B, KB, MB, GB, TB).
 * @returns {number|undefined}
 */
export const toBytes = (strValue, unit) => {
    if (strValue === undefined || strValue === '' || unit === undefined) return undefined;
    const n = typeof strValue === 'number' ? strValue : Number(String(strValue).trim().replace(',', '.'));
    return Number.isFinite(n) ? n * (SIZE_MULTIPLIER[unit] ?? 1) : undefined;
};

/**
 * Function to convert a number value and unit to a multiple of bytes.
 */
export const toBytesMultiplier = (numberValue, unit) => {
    if (numberValue === undefined || numberValue === '' || unit === undefined) return undefined;
    return Number.isFinite(numberValue) ? numberValue / (SIZE_MULTIPLIER[unit] ?? 1) : undefined;
};

export const isNumberLike = (v) => {
    if (typeof v === 'number') return Number.isFinite(v);
    if (typeof v === 'string') {
        const s = v.trim();
        return s !== '' && Number.isFinite(Number(s));
    }
    return false;
};

export const isPlainDict = (v) =>
    v !== null &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    v.constructor === Object;

export const hasValue = (v) => {
    if (v == null) return false;
    if (typeof v === 'string') return v.trim() !== '';
    if (Array.isArray(v)) return v.length > 0;
    if (v instanceof Date) return !isNaN(v.getTime());
    if (typeof v === 'number') return !Number.isNaN(v);
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return false;
};

/**
 * Truncates a list of strings for display. Filters out empty values, concatenates until maxChars,
 * then appends "+X more". Reports items without values separately with a context label.
 * @param {string[]} items
 * @param {number} maxChars
 * @param {string} emptyLabel - context word for items without value (e.g. "volser", "name")
 * @returns {string}
 */
export const truncateList = (items, maxChars = 80, emptyLabel = "value") => {
    if (!items || items.length === 0) return "";
    const valid = items.filter(item => item && item.trim());
    const emptyCount = items.length - valid.length;

    if (valid.length === 0) return emptyCount > 0 ? `+${emptyCount} without ${emptyLabel}` : "";

    let result = "";
    let count = 0;
    for (const item of valid) {
        const candidate = count === 0 ? item : `${result}, ${item}`;
        if (candidate.length > maxChars && count > 0) {
            const remaining = valid.length - count;
            const parts = [`${result} ... +${remaining} more`];
            if (emptyCount > 0) parts.push(`+${emptyCount} without ${emptyLabel}`);
            return parts.join(", ");
        }
        result = candidate;
        count++;
    }
    if (emptyCount > 0) return `${result}, +${emptyCount} without ${emptyLabel}`;
    return result;
};

/**
 * Converts a nested action preferences dict into a human-readable multiline string.
 * One group per line. Boolean sub-options show label only. Non-boolean show label: value.
 * @param {Object} prefs - e.g. {"Cloud Restore": {"Signed URL": true}, "Destruction": {"Method": "shred"}}
 * @returns {string}
 */
export const stringifyPreferences = (prefs) => {
    if (!prefs || Object.keys(prefs).length === 0) return "";

    const truncateValue = (v, max = 40) => {
        const str = String(v);
        if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
            return str.slice(0, 16).replace('T', ' ');
        }
        return str.length > max ? str.slice(0, max) + "..." : str;
    };

    const lines = [];
    Object.entries(prefs).forEach(([group, options]) => {
        const optionParts = Object.entries(options)
            .filter(([_, v]) => v)
            .map(([label, v]) => v === true ? label : `${label}: ${truncateValue(v)}`);
        if (group === "_") {
            // Ungrouped items — print flat, no group heading
            lines.push(...optionParts);
        } else {
            lines.push(optionParts.length > 0 ? `${group} (${optionParts.join(", ")})` : group);
        }
    });
    return lines.join("\n");
};